import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { scheduleAPI, routesAPI } from '@/lib/api';
import { formatDate, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Truck,
  CalendarOff,
  MapPin,
  GripVertical
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

// Draggable Route Card Component
function DraggableRoute({ route, isDragging }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: route.id,
    data: { route },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded px-2 py-1.5 text-xs border border-emerald-200 
        ${isDragging ? 'opacity-50' : 'hover:bg-emerald-50'} 
        transition-colors cursor-grab active:cursor-grabbing group`}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-center gap-1">
        <GripVertical size={12} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-700 truncate">{route.name}</div>
          <div className="text-slate-500">{route.job_count} stops</div>
        </div>
      </div>
    </div>
  );
}

// Route card for drag overlay (shows while dragging)
function RouteOverlay({ route }) {
  return (
    <div className="bg-white rounded px-2 py-1.5 text-xs border-2 border-[#52B8E1] shadow-lg cursor-grabbing">
      <div className="flex items-center gap-1">
        <GripVertical size={12} className="text-[#52B8E1] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-700 truncate">{route.name}</div>
          <div className="text-slate-500">{route.job_count} stops</div>
        </div>
      </div>
    </div>
  );
}

// Droppable Cell Component
function DroppableCell({ driverId, date, isOver, children, isOff, isPastDate }) {
  const { setNodeRef, isOver: isOverThis } = useDroppable({
    id: `${driverId}-${date}`,
    data: { driverId, date },
    disabled: isOff || isPastDate,
  });

  const getBaseStyle = () => {
    if (isOff) return 'bg-amber-50 border-amber-200';
    if (isOverThis) return 'bg-[#52B8E1]/20 border-[#52B8E1] border-2';
    return '';
  };

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg p-2 min-h-[70px] border transition-all ${getBaseStyle()} ${
        !isOff && !isPastDate ? 'hover:border-[#52B8E1]/50' : ''
      }`}
      data-testid={`cell-${driverId}-${date}`}
    >
      {children}
    </div>
  );
}

export default function WeeklySchedule() {
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [activeRoute, setActiveRoute] = useState(null);
  const [updating, setUpdating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Get Monday of the week containing the given date
  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchSchedule();
  }, [weekStart]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await scheduleAPI.getWeekly(weekStart);
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() - 7);
    setWeekStart(date.toISOString().split('T')[0]);
  };

  const goToNextWeek = () => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + 7);
    setWeekStart(date.toISOString().split('T')[0]);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getMonday(new Date()));
  };

  const isToday = (dateStr) => dateStr === getTodayString();
  const isPast = (dateStr) => dateStr < getTodayString();

  const getCellStyle = (day, isOver) => {
    if (isOver) return 'bg-[#52B8E1]/20 border-[#52B8E1]';
    if (day.is_off) return 'bg-amber-50 border-amber-200';
    if (day.routes.length > 0) return 'bg-emerald-50 border-emerald-200';
    if (isPast(day.date)) return 'bg-slate-50 border-slate-200';
    return 'bg-red-50 border-red-200'; // Coverage gap
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveRoute(active.data.current.route);
  };

  // Handle drag end - reassign route
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveRoute(null);

    if (!over) return;

    const routeId = active.id;
    const route = active.data.current.route;
    const [newDriverId, newDate] = over.id.split('-').length > 1 
      ? [over.id.substring(0, over.id.lastIndexOf('-')), over.id.substring(over.id.lastIndexOf('-') + 1)]
      : [null, null];

    // Parse the drop target
    const dropData = over.data.current;
    if (!dropData) return;

    const targetDriverId = dropData.driverId;
    const targetDate = dropData.date;

    // Check if it's a valid drop
    if (!targetDriverId || !targetDate) return;

    // Find current assignment
    let currentDriverId = null;
    let currentDate = null;
    
    for (const driverSchedule of schedule.schedules) {
      for (const day of driverSchedule.days) {
        const foundRoute = day.routes.find(r => r.id === routeId);
        if (foundRoute) {
          currentDriverId = driverSchedule.driver_id;
          currentDate = day.date;
          break;
        }
      }
      if (currentDriverId) break;
    }

    // Also check unassigned routes
    const unassignedRoute = schedule.unassigned_routes?.find(r => r.id === routeId);
    if (unassignedRoute) {
      currentDate = unassignedRoute.date;
    }

    // Skip if dropped in same place
    if (currentDriverId === targetDriverId && currentDate === targetDate) {
      return;
    }

    // Perform the update
    setUpdating(true);
    try {
      const updateData = {
        driver_id: targetDriverId,
      };

      // If date changed, update that too
      if (currentDate !== targetDate) {
        updateData.route_date = targetDate;
      }

      await routesAPI.update(routeId, updateData);
      
      const driverName = schedule.schedules.find(s => s.driver_id === targetDriverId)?.driver_name || 'Unknown';
      toast.success(`Route reassigned to ${driverName} on ${formatDate(targetDate)}`);
      
      // Refresh schedule
      fetchSchedule();
    } catch (error) {
      console.error('Error reassigning route:', error);
      toast.error('Failed to reassign route');
    } finally {
      setUpdating(false);
    }
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveRoute(null);
  };

  return (
    <AdminLayout title="Weekly Schedule">
      {/* Week Navigation */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
            <ChevronLeft size={18} />
          </Button>
          <div className="text-center min-w-[200px]">
            <h2 className="text-lg font-semibold text-slate-900">
              {schedule ? `${formatDate(schedule.week_start)} - ${formatDate(schedule.week_end)}` : 'Loading...'}
            </h2>
          </div>
          <Button variant="outline" size="icon" onClick={goToNextWeek}>
            <ChevronRight size={18} />
          </Button>
          <Button variant="outline" onClick={goToCurrentWeek} className="ml-2">
            This Week
          </Button>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-300"></div>
            <span className="text-slate-600">Has Route</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-100 border border-amber-300"></div>
            <span className="text-slate-600">Day Off</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
            <span className="text-slate-600">No Coverage</span>
          </div>
          <div className="flex items-center gap-2 border-l pl-4 ml-2">
            <GripVertical size={14} className="text-slate-400" />
            <span className="text-slate-600">Drag to reassign</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {schedule && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#52B8E1]/20 rounded-lg">
                  <Truck className="text-[#194461]" size={18} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{schedule.coverage_summary.total_routes_this_week}</p>
                  <p className="text-xs text-slate-600">Routes This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="text-emerald-600" size={18} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{schedule.coverage_summary.days_with_coverage}</p>
                  <p className="text-xs text-slate-600">Driver-Days Covered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <CalendarOff className="text-amber-600" size={18} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{schedule.coverage_summary.days_off}</p>
                  <p className="text-xs text-slate-600">Days Off Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 ${schedule.coverage_summary.coverage_gaps.length > 0 ? 'border-red-300 bg-red-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${schedule.coverage_summary.coverage_gaps.length > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={schedule.coverage_summary.coverage_gaps.length > 0 ? 'text-red-600' : 'text-slate-400'} size={18} />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{schedule.coverage_summary.coverage_gaps.length}</p>
                  <p className="text-xs text-slate-600">Zero Coverage Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Updating indicator */}
      {updating && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center gap-3 shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-[#194461]" />
            <span>Reassigning route...</span>
          </div>
        </div>
      )}

      {/* Schedule Grid with DnD */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : schedule ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 font-semibold text-slate-700 w-48 sticky left-0 bg-slate-50 z-10">
                      Driver
                    </th>
                    {schedule.dates.map((date, i) => (
                      <th 
                        key={date} 
                        className={`text-center p-3 font-semibold w-32 ${
                          isToday(date) ? 'bg-[#52B8E1]/20 text-[#194461]' : 'text-slate-700'
                        }`}
                      >
                        <div className="text-sm">{schedule.day_names[i]}</div>
                        <div className={`text-xs font-normal ${isToday(date) ? 'text-[#194461]' : 'text-slate-500'}`}>
                          {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.schedules.map((driverSchedule) => (
                    <tr key={driverSchedule.driver_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-3 sticky left-0 bg-white z-10 border-r border-slate-100">
                        <div className="font-medium text-slate-900">{driverSchedule.driver_name}</div>
                        {driverSchedule.territory && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin size={10} />
                            {driverSchedule.territory}
                          </div>
                        )}
                      </td>
                      {driverSchedule.days.map((day) => (
                        <td 
                          key={day.date} 
                          className={`p-2 border-l border-slate-100 ${isToday(day.date) ? 'bg-[#52B8E1]/5' : ''}`}
                        >
                          <DroppableCell
                            driverId={driverSchedule.driver_id}
                            date={day.date}
                            isOff={day.is_off}
                            isPastDate={isPast(day.date)}
                          >
                            <div className={`rounded-lg min-h-[60px] ${getCellStyle(day, false)}`}>
                              {day.is_off ? (
                                <div className="flex items-center justify-center h-full p-2">
                                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                    OFF
                                  </Badge>
                                </div>
                              ) : day.routes.length > 0 ? (
                                <div className="space-y-1.5 p-1">
                                  {day.routes.map((route) => (
                                    <TooltipProvider key={route.id}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div>
                                            <DraggableRoute 
                                              route={route} 
                                              isDragging={activeRoute?.id === route.id}
                                            />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <div className="text-xs">
                                            <p className="font-semibold">{route.name}</p>
                                            <p>{route.job_count} stops</p>
                                            <p className="text-slate-400 mt-1">Drag to reassign</p>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full text-slate-400 text-xs p-2">
                                  {isPast(day.date) ? '—' : 'Drop here'}
                                </div>
                              )}
                            </div>
                          </DroppableCell>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeRoute ? <RouteOverlay route={activeRoute} /> : null}
          </DragOverlay>

          {/* Unassigned Routes - Inside the same DndContext */}
          {schedule.unassigned_routes.length > 0 && (
            <Card className="mt-6 border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle size={18} />
                  Unassigned Routes ({schedule.unassigned_routes.length})
                  <span className="text-sm font-normal text-amber-600 ml-2">
                    Drag to the calendar above to assign a driver
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {schedule.unassigned_routes.map((route) => (
                    <DraggableUnassignedRoute key={route.id} route={route} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </DndContext>
      ) : null}

      {/* Coverage Gaps Alert */}
      {schedule && schedule.coverage_summary.coverage_gaps.length > 0 && (
        <Card className="mt-6 border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle size={18} />
              Coverage Gaps - No Drivers Working
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {schedule.coverage_summary.coverage_gaps.map((date) => (
                <Badge key={date} variant="outline" className="bg-red-100 text-red-700 border-red-300">
                  {formatDate(date)}
                </Badge>
              ))}
            </div>
            <p className="text-sm text-red-700 mt-2">
              These dates have no routes assigned to any driver. Consider generating recommendations.
            </p>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

// Draggable component for unassigned routes
function DraggableUnassignedRoute({ route }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: route.id,
    data: { route },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg p-3 border border-amber-200 cursor-grab active:cursor-grabbing 
        ${isDragging ? 'opacity-50' : 'hover:border-amber-400'} transition-colors`}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 text-sm truncate">{route.name}</div>
          <div className="text-xs text-slate-500">{formatDate(route.date)}</div>
          <div className="text-xs text-amber-600 mt-1">{route.job_count} stops • No driver</div>
        </div>
      </div>
    </div>
  );
}
